import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import Pagination from "../../components/ui/Pagination";
import BranchSelect from "../../components/ui/BranchSelect";
import { useLeaveAllocations } from "../../api/queries/leaveQueries";
import useCurrentUser from "../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatPlainDate, todayInManila } from "../../utils/dateTime";
import AllocationDialog from "./components/allocation/AllocationDialog";
import AllocationHistoryDrawer from "./components/allocation/AllocationHistoryDrawer";
import {
  allocationName,
  cycleContaining,
  statusTone,
} from "./components/allocation/allocationFormat";
import "./leave.css";
import { useCanModify } from "../../hooks/usePermissions";
import usePermissions from "../../hooks/usePermissions";

const EMPTY = [];
const FILTER_KEYS = [
  "asOf",
  "search",
  "branchId",
  "leaveTypeId",
  "includeSeparated",
];
const shortDate = (ymd) => formatPlainDate(ymd, "MMM D, YYYY");

/**
 * One policy column cell:
 * - balance → "available / allocated", pending · used, cycle dates (click to adjust)
 * - no balance, eligible → "Set" (creates the current cycle)
 * - not eligible → "Eligible from …" (disabled)
 */
const BalanceCell = ({ balance, eligibility, readOnly, onEdit, typeName }) => {
  if (balance) {
    const content = (
      <>
        <span
          className={`alloc-main tabular-nums ${balance.available < 0 ? "text-danger" : ""}`}
        >
          {balance.available} / {balance.allocated}
        </span>
        <span className="alloc-sub">
          {balance.pending} pending · {balance.used} used
        </span>
        {balance.periodStart && (
          <span className="alloc-cycle">
            {shortDate(balance.periodStart)} – {shortDate(balance.periodEnd)}
          </span>
        )}
      </>
    );
    return readOnly ? (
      <div
        className="alloc-cell alloc-cell--readonly"
        title="You can't change your own allocation"
      >
        {content}
      </div>
    ) : (
      <button
        type="button"
        className="alloc-cell"
        onClick={onEdit}
        aria-label={`Adjust ${typeName}: ${balance.available} of ${balance.allocated} available`}
      >
        {content}
      </button>
    );
  }
  if (eligibility && !eligibility.eligible) {
    return (
      <span
        className="alloc-ineligible"
        title={eligibility.reason || "Not eligible"}
      >
        {eligibility.eligibleFrom
          ? `Eligible from ${shortDate(eligibility.eligibleFrom)}`
          : eligibility.reason || "Not eligible"}
      </span>
    );
  }
  if (readOnly) return <span className="alloc-none">Not allocated</span>;
  return (
    <button
      type="button"
      className="alloc-cell alloc-cell--empty"
      onClick={onEdit}
      aria-label={`Set ${typeName}`}
    >
      <span className="alloc-none">Not allocated</span>
      <span className="alloc-set">Set</span>
    </button>
  );
};

/** Balances of leave types that no longer have an active policy: read-only. */
const OtherBalances = ({ balances }) =>
  balances.length === 0 ? (
    <span className="alloc-none">—</span>
  ) : (
    <div className="d-flex flex-column gap-1">
      {balances.map((b) => (
        <div
          key={b.id}
          className="alloc-legacy"
          title="No active leave policy: read-only"
        >
          <span className="fw-semibold">{b.leaveType?.name || "Leave"}</span>{" "}
          <span className="tabular-nums">
            {b.available} / {b.allocated}
          </span>
        </div>
      ))}
    </div>
  );

/**
 * Admin/HR: each employee's leave per policy, for the anniversary cycle that
 * contains the "as of" date. Cycles are created by the daily replenish job;
 * HR adjusts one cell at a time. Filters live in the URL.
 */
const LeaveAllocationPage = () => {
  // Editing allocations needs Leave: modify (unconfigured roles: Admin/HR, as before)
  const canModify = useCanModify("leave", { company: true });
  const user = useCurrentUser();
  // HR-level (leave with COMPANY scope); role names grant nothing
  const isCompanyWide = usePermissions().companyWide("leave");
  const [params, setParams] = useSearchParams();
  const today = todayInManila();
  const page = Number(params.get("page")) || 1;
  const limit = Number(params.get("limit")) || 20;
  const filters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) || ""])),
    [params],
  );
  const asOf = filters.asOf || today;

  const update = (changes, { resetPage = true } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    if (resetPage && !("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  };

  const [searchText, setSearchText] = useState(filters.search);
  useEffect(() => {
    const value = searchText.trim();
    if (value === filters.search) return undefined;
    const t = setTimeout(() => update({ search: value }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const query = useLeaveAllocations({
    asOf,
    page,
    limit,
    search: filters.search,
    branchId: filters.branchId,
    leaveTypeId: filters.leaveTypeId,
    includeSeparated: filters.includeSeparated === "true",
  });
  const employees = query.data?.data || EMPTY;
  const total = query.data?.meta?.total ?? 0;

  // Columns = leave types with an active policy (from the API). Remember the
  // full list so the type filter keeps all options while one is selected.
  const responseTypes = query.data?.leaveTypes || EMPTY;
  const [allPolicyTypes, setAllPolicyTypes] = useState(EMPTY);
  if (
    !filters.leaveTypeId &&
    responseTypes !== EMPTY &&
    responseTypes.length &&
    responseTypes !== allPolicyTypes
  ) {
    setAllPolicyTypes(responseTypes);
  }
  const columns = responseTypes;
  const columnIds = new Set(columns.map((t) => t.id));
  const hasOtherBalances = employees.some((e) =>
    (e.leaveBalances || []).some(
      (b) => !b.hasPolicy || !columnIds.has(b.leaveTypeId),
    ),
  );

  const [editing, setEditing] = useState(null); // { employee, leaveType, balance, periodStart }
  const [historyFor, setHistoryFor] = useState(null);
  const thisYear = Number(today.slice(0, 4));
  const historyYears = [thisYear + 1, thisYear, thisYear - 1, thisYear - 2];

  if (!isCompanyWide) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="leave-card approval-empty">
            <i className="bi bi-shield-lock"></i>Only Admin and HR can manage
            leave allocations.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0 page-title">
              Leave Allocation
            </h1>
            <p className="text-muted small mb-0">
              Leave renews on each employee's hiring anniversary. Cycles are
              created automatically; click a cell to adjust one.
            </p>
          </div>
          <Link
            to="/leave/replenish"
            className="btn profile-btn profile-btn-white"
          >
            <i className="bi bi-arrow-repeat"></i> Replenish runs
          </Link>
        </div>

        <div className="leave-filters mb-3">
          <div className="d-flex align-items-center gap-2">
            <label
              htmlFor="alloc-asof"
              className="fs-080 text-slate-600 text-nowrap"
            >
              As of
            </label>
            <input
              id="alloc-asof"
              type="date"
              className="filter-input"
              value={asOf}
              onChange={(e) =>
                update({ asOf: e.target.value === today ? "" : e.target.value })
              }
            />
          </div>
          <div className="position-relative leave-filter-search">
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
            <input
              type="search"
              className="filter-input w-100 ps-5"
              placeholder="Name or employee no."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              aria-label="Search name or employee number"
            />
          </div>
          <div className="leave-filter-branch">
            <BranchSelect
              value={filters.branchId}
              onChange={(v) => update({ branchId: v })}
              id="alloc-branch"
            />
          </div>
          <select
            className="status-select"
            value={filters.leaveTypeId}
            onChange={(e) => update({ leaveTypeId: e.target.value })}
            aria-label="Leave policy"
          >
            <option value="">All leave policies</option>
            {(allPolicyTypes.length ? allPolicyTypes : responseTypes).map(
              (t) => (
                <option key={t.id} value={t.id}>
                  {t.policy?.name || t.name}
                </option>
              ),
            )}
          </select>
          <div className="form-check form-switch m-0">
            <input
              id="alloc-separated"
              type="checkbox"
              className="form-check-input"
              checked={filters.includeSeparated === "true"}
              onChange={(e) =>
                update({ includeSeparated: e.target.checked ? "true" : "" })
              }
            />
            <label
              htmlFor="alloc-separated"
              className="form-check-label fs-080"
            >
              Show separated employees
            </label>
          </div>
          {query.isFetching && !query.isLoading && (
            <span
              className="spinner-border spinner-border-sm text-primary ms-auto"
              role="status"
              aria-label="Refreshing"
            ></span>
          )}
        </div>

        {!query.isLoading && !query.isError && columns.length === 0 && (
          <div className="alert alert-warning fs-085" role="status">
            No leave policy is active on {shortDate(asOf)}. Activate a policy
            under Leave Policies to allocate leave.
          </div>
        )}

        <div className="glass-table-card">
          {query.isError ? (
            <div className="approval-empty text-danger">
              <i className="bi bi-exclamation-triangle"></i>
              {getApiErrorMessage(query.error, "Could not load allocations.")}
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table mb-0 leave-table alloc-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Employee no.</th>
                      <th className="py-3">Name</th>
                      <th className="py-3">Branch</th>
                      <th className="py-3">Status</th>
                      {columns.map((t) => (
                        <th key={t.id} className="py-3 alloc-col">
                          {t.policy?.name || t.name}
                          {t.policy && (
                            <div className="alloc-col-sub">
                              {t.policy.annualEntitlement} days · from{" "}
                              {t.policy.minYearsOfService || 0} yr
                            </div>
                          )}
                        </th>
                      ))}
                      {hasOtherBalances && (
                        <th className="py-3 alloc-col">Other (no policy)</th>
                      )}
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {employees.map((emp) => {
                      const isSelf =
                        !!user.employeeId && emp.id === user.employeeId;
                      const balances = emp.leaveBalances || EMPTY;
                      const others = balances.filter(
                        (b) => !b.hasPolicy || !columnIds.has(b.leaveTypeId),
                      );
                      return (
                        <tr
                          key={emp.id}
                          className={isSelf ? "alloc-row-self" : ""}
                        >
                          <td className="ps-4 font-monospace fs-075">
                            {emp.employeeId}
                          </td>
                          <td>
                            <div className="emp-name-text">
                              {allocationName(emp)}
                            </div>
                            <div className="emp-sub-text">
                              {isSelf
                                ? "You"
                                : emp.hiringDate
                                  ? `Hired ${shortDate(emp.hiringDate)}${emp.yearsOfService !== null && emp.yearsOfService !== undefined ? ` · ${emp.yearsOfService} yr` : ""}`
                                  : "No hiring date"}
                            </div>
                          </td>
                          <td>{emp.branch?.branchName || "—"}</td>
                          <td>
                            <span
                              className={`status-pill status-pill--${statusTone(emp.employeeStatus)}`}
                            >
                              {emp.employeeStatus || "—"}
                            </span>
                          </td>
                          {columns.map((t) => {
                            const balance = balances.find(
                              (b) =>
                                b.leaveTypeId === t.id && b.hasPolicy !== false,
                            );
                            return (
                              <td key={t.id}>
                                <BalanceCell
                                  balance={balance}
                                  eligibility={emp.eligibility?.[t.id]}
                                  readOnly={isSelf || !canModify}
                                  typeName={t.policy?.name || t.name}
                                  onEdit={() =>
                                    setEditing({
                                      employee: emp,
                                      leaveType: t,
                                      balance,
                                      periodStart:
                                        balance?.periodStart ||
                                        cycleContaining(emp.hiringDate, asOf)
                                          ?.start ||
                                        null,
                                    })
                                  }
                                />
                              </td>
                            );
                          })}
                          {hasOtherBalances && (
                            <td>
                              <OtherBalances balances={others} />
                            </td>
                          )}
                          <td className="pe-4 text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-light border leave-action-btn"
                              onClick={() => setHistoryFor(emp)}
                            >
                              <i className="bi bi-clock-history me-1"></i>
                              History
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!query.isLoading && employees.length === 0 && (
                      <tr>
                        <td
                          colSpan={6 + columns.length}
                          className="approval-empty"
                        >
                          <i className="bi bi-people"></i>
                          No employees match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {query.isLoading && (
                <div className="text-center py-5">
                  <span
                    className="spinner-border text-primary"
                    role="status"
                    aria-label="Loading"
                  ></span>
                </div>
              )}
              <Pagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={(p) =>
                  update({ page: String(p) }, { resetPage: false })
                }
                onLimitChange={(l) => update({ limit: String(l) })}
              />
            </>
          )}
        </div>
      </div>

      {editing && (
        <AllocationDialog
          employee={editing.employee}
          leaveType={editing.leaveType}
          balance={editing.balance}
          periodStart={editing.periodStart}
          onClose={() => setEditing(null)}
        />
      )}
      {historyFor && (
        <AllocationHistoryDrawer
          employee={historyFor}
          year={thisYear}
          yearOptions={historyYears}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </Layout>
  );
};

export default LeaveAllocationPage;
